import { set } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import { task, dropTask, restartableTask } from 'ember-concurrency';
import type BillingCompanyModel from 'garaje/models/billing-company';
import type CompanyModel from 'garaje/models/company';
import type LocationModel from 'garaje/models/location';
import type TenantModel from 'garaje/models/tenant';
import type TenantConnectionRequestModel from 'garaje/models/tenant-connection-request';
import type UserModel from 'garaje/models/user';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { TenantConnectionStatus } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';
import type { Constructor } from 'type-fest';

export interface CompanyMeta {
  'employees-count': number;
  'active-locations-count': number;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function withUserStateFunctionality<TBase extends Constructor<any>>(Base: TBase) {
  class withUserStateFunctionality extends Base {
    declare currentUser: UserModel | null;
    declare _companyId?: string | null;

    declare currentCompany: CompanyModel | null;
    declare billingCompany: BillingCompanyModel | null;
    declare currentLocation: LocationModel | null;

    declare lastLocationIdLoaded?: string;

    declare tenantConnections: RecordArray<TenantModel> | null;
    declare tenantConnectionRequests: RecordArray<TenantConnectionRequestModel> | null;

    declare _companyMeta: CompanyMeta | null;

    @service declare featureFlags: FeatureFlagsService;
    @service declare store: StoreService;

    initUserState = dropTask(async () => {
      this.currentUser = await this._fetchCurrentUser.perform();
    });

    _setupUserAndCompanyFeatureFlags(currentUser: UserModel, currentCompany: CompanyModel) {
      const properties = {
        ['user']: currentUser,
        ['company']: currentCompany,
      };
      // eslint-disable-next-line ember/use-ember-get-and-set
      this.featureFlags.setProperties(properties);
      this.featureFlags.ready();
    }

    _fetchCurrentUser = restartableTask(async () => {
      return await this.store.queryRecord('user', { id: 'me' });
    });

    loadTenantConnectionRequests = dropTask(async () => {
      this.tenantConnectionRequests = await this.store.query('tenant-connection-request', {
        id: 'my-connection-requests',
        include: 'tenant,connection-request-invites',
      });
    });

    loadTenantConnections = dropTask(async () => {
      this.tenantConnections = await this.store.query('tenant', {
        filter: {
          status: TenantConnectionStatus.CONNECTED,
        },
      });
    });

    // **************************************************************************
    // Visitors specific data
    // **************************************************************************

    loadFlows({ reload = true, locationId = null }: { reload?: boolean; locationId?: string | null } = {}) {
      const lastResponse = this._loadFlowsTask.last;
      // @ts-ignore
      const [lastLocationId] = <[string]>lastResponse?.args ?? [];
      const location = isPresent(locationId) ? locationId : this.currentLocation!.id;
      const isSameLocation = isEmpty(locationId) || lastLocationId === location;
      const useLastResponse = lastResponse && isSameLocation && (lastResponse.isRunning || !reload);

      return useLastResponse ? lastResponse : this._loadFlowsTask.perform(location);
    }

    /**
     * _loadFlowsTask
     */
    _loadFlowsTask = task(async (location: string) => {
      set(this, 'lastLocationIdLoaded', location);

      const includes = ['sign-in-field-actions', 'actionable-sign-in-field-actions', 'actionable-sign-in-fields'];
      const filter = { location, type: 'all' };

      let include = 'agreement-page,';
      include +=
        'active-user-document-template-configurations,active-user-document-template-configurations.user-document-template,';
      include = include + includes.map((include) => `sign-in-field-page.sign-in-fields.${include}`).join(',');

      return await this.store.query('flow', { include, filter });
    });
  }

  return withUserStateFunctionality;
}

export default withUserStateFunctionality;
